import React, { useState, useEffect, useRef } from "react";

function useWindowWidth() {
  const [width, setWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return width;
}

function AISmallIndustriesPage() {
  const [activeService, setActiveService] = useState(null);
  const [activeIndustry, setActiveIndustry] = useState(null);
  const [activeSecurity, setActiveSecurity] = useState(null);
  const [activePhase, setActivePhase] = useState(null);
  const [activeROI, setActiveROI] = useState(null);

  const width = useWindowWidth();
  const isMobile = width < 640;
  const isTablet = width >= 640 && width < 1024;
  const isDesktop = width >= 1024;

  const px = (d, t, m) => (isMobile ? m : isTablet ? t : d);

  const services = [
    {
      tag: "PRODUCTIVITY",
      icon: "🤖",
      title: "INTELLIGENT PROCESS AUTOMATION",
      desc: "We map your repetitive workflows — data entry, approvals, reporting, scheduling — and automate them using AI agents that learn your business rules.",
      roi: "40-60% TIME SAVED ON ADMIN",
      features: [
        "Invoice & purchase order processing",
        "Automated customer follow-ups & emails",
        "Inventory alerts & reorder triggers",
        "Report generation on a schedule",
      ],
    },
    {
      tag: "INTELLIGENCE",
      icon: "💬",
      title: "AI-POWERED CUSTOMER SUPPORT",
      desc: "Deploy a smart assistant trained on your product catalog, FAQs, and policies — available 24/7 across chat, WhatsApp, email, or your website.",
      roi: "3× FASTER RESPONSE, LOWER STAFFING COSTS",
      features: [
        "Handles 70–80% of tier-1 queries",
        "Escalates complex cases to humans",
        "Learns from your past support tickets",
        "Multilingual support out of the box",
      ],
    },
    {
      tag: "ANALYTICS",
      icon: "📈",
      title: "BUSINESS INTELLIGENCE DASHBOARDS",
      desc: "We turn your spreadsheets, ERP data, and transaction records into living dashboards with AI-generated insights and plain-English summaries.",
      roi: "BETTER DECISIONS, 2× FASTER",
      features: [
        "Sales forecasting & demand prediction",
        "Profit margin analysis by product/line",
        "Cash flow alerts and anomaly detection",
        "Ask your data questions in plain English (NLP)",
      ],
    },
    {
      tag: "CONTENT",
      icon: "✍️",
      title: "AI CONTENT & MARKETING ENGINE",
      desc: "Generate product descriptions, social posts, email campaigns, and proposals at scale — all in your brand voice, reviewed before publishing.",
      roi: "10× CONTENT OUTPUT, SAME TEAM",
      features: [
        "Product catalog content generation",
        "SEO-optimized blog & website copy",
        "Tender / proposal document drafting",
        "Multi-channel campaign calendars",
      ],
    },
    {
      tag: "OPERATIONS",
      icon: "🏭",
      title: "SUPPLY CHAIN & INVENTORY AI",
      desc: "Predict stockouts, optimize reorder points, and flag supplier delays before they hit your production line — using your existing data.",
      roi: "20-35% INVENTORY COST REDUCTION",
      features: [
        "Demand sensing & seasonal forecasting",
        "Supplier risk scoring",
        "Dead stock & waste reduction alerts",
        "Integrated with QuickBooks, Tally, SAP",
      ],
    },
    {
      tag: "COMPLIANCE",
      icon: "📋",
      title: "DOCUMENT INTELLIGENCE & COMPLIANCE",
      desc: "Extract, classify, and summarize contracts, invoices, and regulatory documents. Flag risks before they become problems.",
      roi: "90% LESS MANUAL DOCUMENT REVIEW",
      features: [
        "Contract clause extraction & review",
        "GST/VAT reconciliation automation",
        "Regulatory document monitoring",
        "Searchable document archive with AI",
      ],
    },
  ];

  const industries = [
    { icon: "🏗️", title: "Construction & Contracting" },
    { icon: "🏭", title: "Light Manufacturing" },
    { icon: "🛒", title: "Retail & E-Commerce" },
    { icon: "🍽️", title: "Food & Beverage" },
    { icon: "🚚", title: "Logistics & Distribution" },
    { icon: "⚕️", title: "Healthcare Clinics" },
    { icon: "⚖️", title: "Legal & Professional Services" },
    { icon: "🏠", title: "Real Estate Agencies" },
    { icon: "🎓", title: "Education & Training" },
    { icon: "💼", title: "Accounting & Finance" },
    { icon: "🔧", title: "Field Services & Maintenance" },
    { icon: "🌾", title: "Agri-Business" },
  ];

  const roiItems = [
    {
      stat: "8 WEEKS",
      label: "First Value Milestone",
      desc: "Our rapid deployment model delivers a working, measurable AI solution in 8 weeks or less — not months of consultancy fees.",
    },
    {
      stat: "3-5×",
      label: "Return on Spend",
      desc: "Clients typically see $3–$5 in value (time saved, errors reduced, revenue gained) for every $1 spent with SurensInffotek.",
    },
    {
      stat: "60%",
      label: "Reduction in Manual Work",
      desc: "On average, 60% of targeted manual tasks are eliminated or significantly reduced within the first 3 months.",
    },
    {
      stat: "ZERO",
      label: "Hidden Costs",
      desc: "Fixed-scope projects with transparent pricing. No surprise retainers. No lock-in after delivery.",
    },
  ];

  const securityItems = [
    {
      icon: "🏢",
      title: "ON-PREMISE OPTION",
      desc: "Deploy AI models entirely within your own infrastructure. Your data never leaves your network. Available for sensitive industries like legal, healthcare, and manufacturing.",
    },
    {
      icon: "🔐",
      title: "NO TRAINING ON YOUR DATA",
      desc: "We use foundation models via private API — your business data is never used to train public models. Your proprietary information stays proprietary.",
    },
    {
      icon: "📜",
      title: "COMPLIANCE-READY",
      desc: "Built with GDPR, PDPA, and local data protection standards in mind. We provide a data processing agreement (DPA) with every engagement.",
    },
    {
      icon: "🔑",
      title: "ROLE-BASED ACCESS",
      desc: "Granular access controls so the right people see the right data. Audit logs on every AI action for full traceability and accountability.",
    },
    {
      icon: "🛡️",
      title: "ENCRYPTED AT EVERY STEP",
      desc: "TLS in transit, AES-256 at rest. All integrations use OAuth2 or API key vaulting — no plain-text credentials anywhere in your stack.",
    },
    {
      icon: "🤝",
      title: "YOU OWN THE OUTPUT",
      desc: "All AI models fine-tuned or customized for your business are fully owned by you. No vendor lock-in, full source code handover available.",
    },
  ];

  const borderL = px("10px solid #10c9b5", "6px solid #10c9b5", "4px solid #10c9b5");
  const borderR = px("10px solid #10c9b5", "6px solid #10c9b5", "4px solid #10c9b5");

  return (
    <div style={{ fontFamily: "'Inter', sans-serif", background: "#f4f4f4" }}>

      {/* ── HERO ── */}
      <div
        style={{
          minHeight: isMobile ? "auto" : "100vh",
          background: "linear-gradient(135deg, #10c9b5 0%, #173b72 100%)",
          padding: px("70px 60px 80px", "50px 36px 60px", "36px 20px 48px"),
          color: "#fff",
          borderLeft: borderL,
          borderRight: borderR,
          borderTop: borderL, 
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative circles — large outer */}
        <div
          style={{
            position: "absolute",
            top: "25%",
            right: px("-120px", "-100px", "-140px"),
            transform: "translateY(-50%)",
            width: px("500px", "440px", "320px"),
            height: px("450px", "440px", "320px"),
            borderRadius: "50%",
            border: "1px solid rgba(255,255,255,0.15)",
            pointerEvents: "none",
          }}
        />
        {/* Decorative circles — inner smaller */}
        <div
          style={{
            position: "absolute",
            top: "25%",
            right: px("-20px", "-10px", "-60px"),
            transform: "translateY(-50%)",
            width: px("300px", "280px", "200px"),
            height: px("280px", "280px", "200px"),
            borderRadius: "50%",
            border: "1px solid rgba(255,255,255,0.12)",
            pointerEvents: "none",
          }}
        />

        <div
          style={{
            border: "1px solid rgba(255,255,255,0.4)",
            display: "inline-block",
            padding: "10px 16px",
            borderRadius: "8px",
            letterSpacing: "4px",
            fontWeight: "700",
            fontSize: "10px",
            marginBottom: "35px",
          }}
        >
          SurensInffotek — AI SOLUTIONS PRACTICE
        </div>

        <h1
          style={{
            fontFamily: "'Bebas Neue', sans-serif",
            fontSize: px("110px", "72px", "56px"),
            lineHeight: "1",
            letterSpacing: "1.4px",
            margin: 0,
            maxWidth: isMobile ? "100%" : "620px",
          }}
        >
          AI THAT WORKS FOR{" "}
          <span style={{ color: "ffff" }}>SMALL</span>{" "}
          INDUSTRIES
        </h1>

        <p
          style={{
            fontSize: px("18px", "16px", "15px"),
            lineHeight: "1.8",
            maxWidth: "600px",
            marginTop: "35px",
            color: "rgba(255,255,255,0.9)",
          }}
        >
          Practical, affordable, and secure AI implementations that deliver
          measurable ROI — designed specifically for small and mid-sized
          businesses ready to compete smarter.
        </p>

        {/* STATS */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(4,auto)",
            gap: px("60px", "36px", "24px"),
            marginTop: "60px",
          }}
        >
          {[
            ["3×", "FASTER DELIVERY"],
            ["60%", "COST REDUCTION"],
            ["ZERO", "DATA SOLD OR SHARED"],
            ["8 WEEKS", "FIRST ROI TARGET"],
          ].map((item, i) => (
            <div key={i}>
              <div
                style={{
                  fontFamily: "'Bebas Neue', sans-serif",
                  fontSize: px("39px", "32px", "28px"),
                }}
              >
                {item[0]}
              </div>
              <div
                style={{
                  letterSpacing: "2px",
                  fontWeight: "400",
                  opacity: 0.9,
                  fontSize: "10px",
                }}
              >
                {item[1]}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── PROBLEMS ── */}
      <div
        style={{
          background: "#143847",
          color: "#fff",
          borderLeft: borderL,
          borderRight: borderR,
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile
              ? "1fr"
              : isTablet
              ? "repeat(2,1fr)"
              : "repeat(4,1fr)",
          }}
        >
          {[
            {
              icon: "⚙️",
              title: "MANUAL BOTTLENECKS",
              text: "Repetitive tasks eat into margins and keep skilled people stuck on low-value work every day.",
            },
            {
              icon: "📊",
              title: "DATA BLIND SPOTS",
              text: "Business data exists but isn't being used — decisions made on instinct instead of insight.",
            },
            {
              icon: "🏁",
              title: "SPEED TO MARKET",
              text: "Larger competitors move faster. AI closes the gap without requiring enterprise-level headcount.",
            },
            {
              icon: "🔒",
              title: "AI FEAR & RISK",
              text: "Worried about data privacy, vendor lock-in, or solutions that don't fit your actual workflow.",
            },
          ].map((item, i, arr) => (
            <div
              key={i}
              style={{
                padding: px("30px", "24px", "20px"),
                borderRight:
                  !isMobile && i !== arr.length - 1
                    ? "1px solid rgba(255,255,255,0.1)"
                    : "none",
                borderBottom:
                  isMobile && i !== arr.length - 1
                    ? "1px solid rgba(255,255,255,0.1)"
                    : isTablet && i < 2
                    ? "1px solid rgba(255,255,255,0.1)"
                    : "none",
              }}
            >
              <div style={{ fontSize: "30px", marginBottom: "20px" }}>{item.icon}</div>
              <h2
                style={{
                  color: "#10c9b5",
                  fontFamily: "'Bebas Neue', sans-serif",
                  fontSize: "20px",
                  marginBottom: "8px",
                  letterSpacing: "1px",
                }}
              >
                {item.title}
              </h2>
              <p
                style={{
                  lineHeight: "1.7",
                  fontSize: "13px",
                  maxWidth: "220px",
                  color: "rgba(255,255,255,0.8)",
                  margin: 0,
                }}
              >
                {item.text}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* ── SERVICES ── */}
      <div
        style={{
          background: "#f4f4f4",
          padding: px("85px 60px", "60px 36px", "40px 20px"),
          borderLeft: borderL,
          borderRight: borderR,
        }}
      >
        <div
          style={{
            color: "#10c9b5",
            letterSpacing: "2.9px",
            fontWeight: "700",
            marginBottom: "18px",
            fontSize: "17px",
          }}
        >
          // WHAT WE BUILD
        </div>

        <h1
          style={{
            fontFamily: "'Bebas Neue', sans-serif",
            fontSize: px("70px", "52px", "38px"),
            color: "#132b5b",
            margin: 0,
          }}
        >
          OUR AI SERVICES
        </h1>

        <p
          style={{
            fontSize: px("16px", "15px", "14px"),
            color: "#3c4d67",
            maxWidth: "700px",
            lineHeight: "1.7",
            marginTop: "18px",
          }}
        >
          Six battle-tested service lines — each scoped for small industry
          budgets, each measured by outcomes you can take to your P&L.
        </p>

        <div
          style={{
            marginTop: "55px",
            display: "grid",
            gridTemplateColumns: isMobile
              ? "1fr"
              : isTablet
              ? "repeat(2,1fr)"
              : "repeat(3,1fr)",
          }}
        >
          {services.map((item, index) => {
            const isActive = activeService === index;
            return (
              <div
                key={index}
                onClick={() => setActiveService(isActive ? null : index)}
                style={{
                  cursor: "pointer",
                  position: "relative",
                  overflow: "hidden",
                  padding: px("32px", "26px", "20px"),
                  minHeight: px("430px", "380px", "auto"),
                  transition: "all 0.35s ease",
                  background: isActive ? "#132b5b" : "#fff",
                  color: isActive ? "#fff" : "#132b5b",
                  border: isActive
                    ? "2px solid #10c9b5"
                    : "1px solid #ddd",
                  boxShadow: isActive
                    ? "0 8px 32px rgba(16,201,181,0.18)"
                    : "0 2px 8px rgba(0,0,0,0.04)",
                  transform: isActive ? "translateY(-4px)" : "none",
                }}
              >
                {/* BIG NUMBER */}
                <div
                  style={{
                    position: "absolute",
                    top: "18px",
                    right: "24px",
                    fontSize: px("120px", "90px", "70px"),
                    fontFamily: "'Bebas Neue', sans-serif",
                    lineHeight: "1",
                    zIndex: 0,
                    color: isActive
                      ? "rgba(255,255,255,0.06)"
                      : "rgba(16,201,181,0.10)",
                    userSelect: "none",
                  }}
                >
                  {String(index + 1).padStart(2, "0")}
                </div>

                <div style={{ position: "relative", zIndex: 1 }}>
                  <div
                    style={{
                      border: "1px solid #10c9b5",
                      display: "inline-block",
                      padding: "8px 20px",
                      letterSpacing: "3px",
                      fontWeight: "700",
                      color: "#10c9b5",
                      fontSize: "11px",
                    }}
                  >
                    {item.tag}
                  </div>

                  <div style={{ fontSize: "30px", marginTop: "28px" }}>{item.icon}</div>

                  <h2
                    style={{
                      fontFamily: "'Bebas Neue', sans-serif",
                      fontSize: px("28px", "24px", "22px"),
                      lineHeight: "1.1",
                      fontWeight: "400",
                      marginTop: "18px",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {item.title}
                  </h2>

                  <p style={{ fontSize: "14px", lineHeight: "1.8", marginTop: "12px" }}>
                    {item.desc}
                  </p>

                  <div style={{ marginTop: "20px", marginBottom: "30px" }}>
                    {item.features?.map((feature, idx) => (
                      <div
                        key={idx}
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "10px",
                          marginBottom: "12px",
                          fontSize: "14px",
                          lineHeight: "1.7",
                          color: isActive ? "rgba(255,255,255,0.88)" : "#3c4d67",
                        }}
                      >
                        <span
                          style={{
                            color: "#10c9b5",
                            fontWeight: "700",
                            fontSize: "16px",
                            flexShrink: 0,
                            marginTop: "1px",
                          }}
                        >
                          →
                        </span>
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>

                  <div
                    style={{
                      borderTop: isActive
                        ? "1px solid rgba(255,255,255,0.18)"
                        : "1px solid #e0e0e0",
                      paddingTop: "22px",
                    }}
                  >
                    <div
                      style={{
                        letterSpacing: "3px",
                        marginBottom: "10px",
                        opacity: 0.7,
                        fontSize: "10px",
                      }}
                    >
                      TYPICAL ROI IMPACT
                    </div>
                    <div
                      style={{
                        color: "#10c9b5",
                        fontFamily: "'Bebas Neue', sans-serif",
                        fontSize: "22px",
                        letterSpacing: "1px",
                      }}
                    >
                      {item.roi}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── ROI ── */}
      <div
        style={{
          padding: px("85px 60px", "60px 36px", "40px 20px"),
          borderLeft: borderL,
          borderRight: borderR,
          background: "linear-gradient(135deg, #10c9b5 0%, #173b72 100%)",
          color: "#fff",
          position: "relative",
          overflow: "hidden",
        }}
      >
  
        

        <div
          style={{
            letterSpacing: "6px",
            fontWeight: "700",
            marginBottom: "18px",
            fontSize: "13px",
            opacity: 0.9,
          }}
        >
          // MEASURABLE OUTCOMES
        </div>

        <h1
          style={{
            fontFamily: "'Bebas Neue', sans-serif",
            fontSize: px("78px", "56px", "40px"),
            margin: 0,
            lineHeight: "1",
          }}
        >
          WHAT ROI LOOKS LIKE
        </h1>

        <p
          style={{
            fontSize: px("16px", "15px", "14px"),
            lineHeight: "1.8",
            maxWidth: "600px",
            marginTop: "22px",
            color: "rgba(255,255,255,0.85)",
          }}
        >
          We measure success in business metrics — not model accuracy. Every
          engagement includes a 90-day ROI review.
        </p>

        <div
          style={{
            marginTop: "50px",
            display: "grid",
            gridTemplateColumns: isMobile
              ? "repeat(2,1fr)"
              : isTablet
              ? "repeat(2,1fr)"
              : "repeat(4,1fr)",
            gap: "20px",
          }}
        >
          {roiItems.map((item, i) => {
            return (
              <div
                key={i}
                style={{
                  padding: px("30px", "24px", "18px"),
                  border: "1px solid rgba(255,255,255,0.25)",
                  background: "rgba(255,255,255,0.08)",
                }}
              >
                <div
                  style={{
                    fontFamily: "'Bebas Neue', sans-serif",
                    fontSize: px("52px", "40px", "34px"),
                    lineHeight: "1",
                  }}
                >
                  {item.stat}
                </div>
                <div
                  style={{
                    fontSize: px("18px", "15px", "14px"),
                    fontWeight: "700",
                    marginTop: "12px",
                    marginBottom: "14px",
                  }}
                >
                  {item.label}
                </div>
                <p
                  style={{
                    fontSize: "13px",
                    lineHeight: "1.8",
                    color: "rgba(255,255,255,0.82)",
                    margin: 0,
                  }}
                >
                  {item.desc}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── SECURITY ── */}
      <div
        style={{
          background: "#132b5b",
          color: "#fff",
          padding: px("90px 60px", "60px 36px", "40px 20px"),
          position: "relative",
          overflow: "hidden",
          borderLeft: borderL,
          borderRight: borderR,
        }}
      >
       
        <div
          style={{
            color: "#10c9b5",
            letterSpacing: "6px",
            marginBottom: "16px",
            fontWeight: "700",
            fontSize: "13px",
          }}
        >
          // TRUST & SECURITY
        </div>

        <h1
          style={{
            fontFamily: "'Bebas Neue', sans-serif",
            fontSize: px("60px", "46px", "34px"),
            lineHeight: "0.95",
            margin: 0,
            color: "#fff",
          }}
        >
          YOUR DATA. YOUR RULES.
        </h1>

        <p
          style={{
            fontSize: px("16px", "15px", "14px"),
            lineHeight: "1.9",
            color: "rgba(255,255,255,0.78)",
            maxWidth: "550px",
            marginTop: "25px",
          }}
        >
          Small businesses handle sensitive data — client records, financial
          data, trade information. We build AI solutions that protect it, not
          expose it.
        </p>

        <div
          style={{
            marginTop: "70px",
            display: "grid",
            gridTemplateColumns: isMobile
              ? "1fr"
              : isTablet
              ? "repeat(2,1fr)"
              : "repeat(4,1fr)",
            borderTop: "1px solid rgba(255,255,255,0.08)",
            borderLeft: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          {securityItems.map((item, i) => {
            const isActive = activeSecurity === i;
            return (
              <div
                key={i}
                onClick={() => setActiveSecurity(isActive ? null : i)}
                style={{
                  padding: px("38px 30px", "28px 22px", "22px 16px"),
                  minHeight: px("240px", "200px", "auto"),
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  borderRight: "1px solid rgba(255,255,255,0.08)",
                  borderBottom: "1px solid rgba(255,255,255,0.08)",
                background: isActive ? "#1d376d" : "transparent",
                  boxShadow: isActive
                    ? "inset 0 0 0 2px #10c9b5"
                    : "none",
                  transform: isActive ? "scale(1.01)" : "none",
                }}
              >
                <div style={{ fontSize: "26px", marginBottom: "24px" }}>
                  {item.icon}
                </div>
                <h2
                  style={{
                    color: "#10c9b5",
                    fontFamily: "'Bebas Neue', sans-serif",
                    fontSize: "20px",
                    lineHeight: "1",
                    marginBottom: "18px",
                    letterSpacing: "1px",
                  }}
                >
                  {item.title}
                </h2>
                <p
                  style={{
                    color: isActive
                      ? "rgba(255,255,255,0.9)"
                      : "rgba(255,255,255,0.65)",
                    lineHeight: "1.9",
                    fontSize: "14px",
                    maxWidth: "320px",
                    margin: 0,
                    transition: "color 0.3s",
                  }}
                >
                  {item.desc}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── PROCESS TIMELINE ── */}
      <div
        style={{
          background: "#f4f4f4",
          padding: px("95px 60px 110px", "60px 36px 70px", "40px 20px 50px"),
          borderLeft: borderL,
          borderRight: borderR,
        }}
      >
        <div
          style={{
            color: "#007f6d",
            letterSpacing: "6px",
            fontWeight: "700",
            fontSize: "13px",
            marginBottom: "18px",
          }}
        >
          // HOW WE WORK
        </div>

        <h1
          style={{
            fontFamily: "'Bebas Neue', sans-serif",
            fontSize: px("62px", "48px", "36px"),
            color: "#132b5b",
            lineHeight: "0.95",
            margin: 0,
          }}
        >
          OUR ENGAGEMENT MODEL
        </h1>

        <p
          style={{
            fontSize: px("16px", "15px", "14px"),
            lineHeight: "1.9",
            color: "#4a5c74",
            maxWidth: "600px",
            marginTop: "28px",
          }}
        >
          A proven 5-phase process that minimizes risk and gets you to value
          fast — with room to scale at every checkpoint.
        </p>

        <div
          style={{
            marginTop: "90px",
            position: "relative",
          }}
        >
          {/* CONNECTOR LINE — desktop/tablet */}
          {!isMobile && (
            <div
              style={{
                position: "absolute",
                top: "30px",
                left: "calc(10% + 30px)",
                right: "calc(10% + 30px)",
                height: "2px",
                background: "linear-gradient(90deg, #10c9b5, #b7d9d6)",
                zIndex: 0,
              }}
            />
          )}

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile
                ? "1fr"
                : isTablet
                ? "repeat(3,1fr)"
                : "repeat(5,1fr)",
              gap: px("10px", "24px", "24px"),
              position: "relative",
              zIndex: 1,
            }}
          >
            {[
              {
                icon: "🔍",
                phase: "PHASE 01",
                title: "DISCOVERY",
                desc: "Free 2–hour audit of your current workflows and data. We identify the highest-ROI AI opportunity in your business.",
              },
              {
                icon: "🗺️",
                phase: "PHASE 02",
                title: "BLUEPRINT",
                desc: "We deliver a clear AI roadmap: what we'll build, what it will cost, what ROI to expect, and how your data is protected.",
              },
              {
                icon: "🛠️",
                phase: "PHASE 03",
                title: "BUILD",
                desc: "Rapid 4–8 week development sprint. Weekly demos. You see progress, not just promises. Fully iterative.",
              },
              {
                icon: "🚀",
                phase: "PHASE 04",
                title: "DEPLOY",
                desc: "Hands-on rollout with your team. Training sessions. Documentation. We don't hand over and disappear.",
              },
              {
                icon: "📈",
                phase: "PHASE 05",
                title: "OPTIMIZE",
                desc: "90-day post-launch review. ROI measurement. Tuning based on real usage. Scale when you're ready.",
              },
            ].map((item, i) => {
              const isActive = activePhase === i;
              return (
                <div
                  key={i}
                  onClick={() => setActivePhase(isActive ? null : i)}
                  style={{
                    textAlign: isMobile ? "left" : "center",
                    cursor: "pointer",
                    display: isMobile ? "flex" : "block",
                    gap: isMobile ? "18px" : "0",
                    alignItems: isMobile ? "flex-start" : "initial",
                    marginBottom: isMobile ? "28px" : "0",
                  }}
                >
                  {/* ICON CIRCLE */}
                  <div
                    style={{
                      width: "60px",
                      height: "60px",
                      flexShrink: 0,
                      borderRadius: "50%",
                      border: isActive
                        ? "3px solid #10c9b5"
                        : "3px solid #b7d9d6",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: isMobile ? "0" : "0 auto",
                      fontSize: "25px",
                      background: isActive ? "#10c9b5" : "#f4f4f4",
                      transition: "all 0.3s ease",
                      boxShadow: isActive
                        ? "0 0 0 6px rgba(16,201,181,0.18)"
                        : "none",
                    }}
                  >
                    {item.icon}
                  </div>

                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        marginTop: isMobile ? "2px" : "34px",
                        color: "#10c9b5",
                        letterSpacing: "4px",
                        fontWeight: "800",
                        fontSize: "10px",
                      }}
                    >
                      {item.phase}
                    </div>

                    <h2
                      style={{
                        fontFamily: "'Bebas Neue', sans-serif",
                        color: isActive ? "#10c9b5" : "#132b5b",
                        fontSize: px("28px", "24px", "22px"),
                        marginTop: "14px",
                        marginBottom: "18px",
                        letterSpacing: "1px",
                        transition: "color 0.3s",
                      }}
                    >
                      {item.title}
                    </h2>

                    <p
                      style={{
                        color: isActive ? "#132b5b" : "#52657d",
                        lineHeight: "1.9",
                        fontSize: "14px",
                        maxWidth: isMobile ? "100%" : "270px",
                        fontWeight: "500",
                        margin: isMobile ? "0" : "0 auto",
                        transition: "color 0.3s",
                      }}
                    >
                      {item.desc}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── INDUSTRIES ── */}
      <div
        style={{
          background: "#edf2f1",
          padding: px("85px 60px", "60px 36px", "40px 20px"),
          borderLeft: borderL,
          borderRight: borderR,
        }}
      >
        <div
          style={{
            color: "#10c9b5",
            letterSpacing: "6px",
            fontWeight: "700",
            fontSize: "13px",
          }}
        >
          // WHO WE SERVE
        </div>

        <h1
          style={{
            fontFamily: "'Bebas Neue', sans-serif",
            fontSize: px("65px", "48px", "36px"),
            color: "#132b5b",
            marginTop: "20px",
            marginBottom: "8px",
          }}
        >
          INDUSTRIES WE TARGET
        </h1>

        <p
          style={{
            fontSize: px("16px", "15px", "14px"),
            lineHeight: "1.9",
            color: "#4f667d",
            maxWidth: "550px",
            marginTop: "0",
            marginBottom: "40px",
            fontWeight: "400",
          }}
        >
          Any small business with repetitive processes, customer data, or
          physical inventory is a candidate. Here are our primary verticals.
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile
              ? "1fr"
              : isTablet
              ? "repeat(2,1fr)"
              : "repeat(4,1fr)",
            gap: "14px",
          }}
        >
          {industries.map((item, index) => {
            const isActive = activeIndustry === index;
            return (
              <div
                key={index}
                onClick={() => setActiveIndustry(isActive ? null : index)}
                style={{
                  cursor: "pointer",
                  padding: "18px 22px",
                  background: isActive ? "#10c9b5" : "#fff",
                  color: isActive ? "#fff" : "#132b5b",
                  border: isActive
                    ? "2px solid #0db5a3"
                    : "1px solid #d7e7e4",
                  fontSize: "15px",
                  fontWeight: "600",
                  transition: "all 0.25s ease",
                  display: "flex",
                  alignItems: "center",
                  gap: "14px",
                  transform: isActive ? "translateY(-2px)" : "none",
                  boxShadow: isActive
                    ? "0 6px 18px rgba(16,201,181,0.25)"
                    : "0 1px 4px rgba(0,0,0,0.05)",
                }}
              >
                <span style={{ fontSize: "22px" }}>{item.icon}</span>
                <span>{item.title}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── CTA ── */}
      <div
        style={{
          background: "#143847",
          padding: px("70px 60px", "50px 36px", "40px 20px"),
          color: "#fff",
          borderLeft: borderL,
          borderRight: borderR,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative dots pattern */}
        <div
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            backgroundImage:
              "radial-gradient(rgba(16,201,181,0.08) 1px, transparent 1px)",
            backgroundSize: "28px 28px",
            pointerEvents: "none",
          }}
        />

        <div style={{ position: "relative", zIndex: 1 }}>
          <h1
            style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: px("62px", "44px", "32px"),
              maxWidth: "900px",
              lineHeight: "1",
              margin: 0,
            }}
          >
            READY TO SEE WHAT AI CAN DO FOR YOUR BUSINESS?
          </h1>

          <p
            style={{
              fontSize: px("16px", "15px", "14px"),
              maxWidth: "500px",
              lineHeight: "1.8",
              marginTop: "28px",
              color: "rgba(255,255,255,0.8)",
            }}
          >
            Book a free 2–hour workflow audit with SurensInffotek. No commitment,
            no jargon — just a clear picture of your biggest AI opportunity and
            what it's worth.
          </p>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              marginTop: "44px",
              maxWidth: "380px",
            }}
          >
            <button
              style={{
                background: "#132b5b",
                color: "#fff",
                border: "2px solid #132b5b",
                padding: px("18px 40px", "16px 34px", "14px 28px"),
                fontSize: px("16px", "15px", "14px"),
                fontWeight: "700",
                cursor: "pointer",
                borderRadius: "4px",
                width: "100%",
                transition: "all 0.25s ease",
                letterSpacing: "0.5px",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#10c9b5";
                e.currentTarget.style.borderColor = "#10c9b5";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#132b5b";
                e.currentTarget.style.borderColor = "#132b5b";
              }}
            >
              Book Free AI Audit
            </button>

            <button
              style={{
                background: "transparent",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.4)",
                padding: px("18px 40px", "16px 34px", "14px 28px"),
                fontSize: px("16px", "15px", "14px"),
                fontWeight: "700",
                cursor: "pointer",
                borderRadius: "4px",
                width: "100%",
                transition: "all 0.25s ease",
                letterSpacing: "0.5px",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.1)";
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.8)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.4)";
              }}
            >
              Download Service Brochure
            </button>
          </div>
        </div>
      </div>

      {/* ── FOOTER BRAND ── */}
      <div style={{ background: "#1c3461" }}>
        <div
          style={{
            background: "#1c3461",
            borderLeft: borderL,
            borderRight: borderR,
            borderTop: "4px solid #10c9b5",
            padding: px("45px 70px", "36px 36px", "28px 20px"),
          }}
        >
          <h2
            style={{
              color: "#fff",
              fontSize: "22px",
              fontFamily: "'Bebas Neue', sans-serif",
              letterSpacing: "3px",
              margin: 0,
              lineHeight: 1,
            }}
          >
            SurensInffotek
          </h2>
        </div>
      </div>
    </div>
  );
}

export default AISmallIndustriesPage;
